import { useState } from 'react';
import { Button, Card, Space, Statistic, Table, Typography, message, theme } from 'antd';
import { DatabaseOutlined, DownloadOutlined, PushpinOutlined, SaveOutlined } from '@ant-design/icons';
import { Column, Line, Pie, Area } from '@ant-design/charts';
import { Link } from 'react-router-dom';
import { useCurrency } from '@/contexts/CurrencyContext';
import { downloadAssistantTableXlsx, downloadAssistantTablePdf } from '@/services/aiAgent.service';
import { SummaryCard } from './SummaryCard';
import { isDocumentSummary, type DocumentSummary } from '../../types/ai-document-summary';
import { HowToGuideCard } from '@/components/help/HowToGuideCard';
import { formatDate, formatNumber } from '@/utils/format';
import { isHowToGuidePayload, type HowToGuidePayload } from '@/types/help-guide';

const { Text, Paragraph } = Typography;

function humanizeQueryIntent(intent: string): string {
  return intent
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function isQueryErpDataRow(data: unknown): data is { intent: string; count?: number; ok?: boolean; filters?: unknown } {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  const o = data as Record<string, unknown>;
  return typeof o.intent === 'string';
}

type AssistantTableColumn = {
  key: string;
  label: string;
  type?: 'text' | 'currency' | 'date' | 'status' | 'number';
};

type AssistantDataTable = {
  title?: string;
  columns: AssistantTableColumn[];
  rows: Record<string, unknown>[];
};

type AssistantChartSpec = {
  type: 'bar' | 'line' | 'pie' | 'donut' | 'area' | 'stacked';
  xField?: string;
  yField?: string;
  angleField?: string;
  colorField?: string;
  data: Record<string, unknown>[];
  title?: string;
};

type AssistantKpiCard = {
  label: string;
  value: number | string;
  format?: 'number' | 'currency';
};

type AssistantRichResponse = {
  intent?: string;
  answerText?: string;
  dataTable?: AssistantDataTable;
  chartSpec?: AssistantChartSpec;
  kpiCards?: AssistantKpiCard[];
  links?: Array<{ label?: string; path?: string }>;
  suggestedActions?: Array<string | { label?: string; path?: string; prompt?: string }>;
  exportOptions?: string[];
  warnings?: string[];
  sourceContext?: Record<string, unknown>;
  howTo?: HowToGuidePayload;
};

/** `openPage` / `deepLinkToDocument` / `openFilteredList` results. */
function asNavigationResult(data: unknown): { path: string; label: string; description?: string } | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const o = data as Record<string, unknown>;
  const path = typeof o.path === 'string' ? o.path.trim() : '';
  if (!path.startsWith('/') || path.includes('/:')) return null;
  const label = typeof o.label === 'string' && o.label.trim() ? o.label.trim() : path;
  const description = typeof o.description === 'string' ? o.description : undefined;
  return { path, label, description };
}

function isAssistantRichResponse(data: unknown): data is AssistantRichResponse {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.answerText === 'string' ||
    d.dataTable !== undefined ||
    d.chartSpec !== undefined ||
    d.kpiCards !== undefined
  );
}

function escapeCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function downloadAssistantTableCsv(dataTable: AssistantDataTable, filenameBase: string): void {
  const header = dataTable.columns.map((c) => escapeCsvCell(c.label)).join(',');
  const lines = dataTable.rows.map((row) =>
    dataTable.columns
      .map((c) => escapeCsvCell(formatCellValue(row[c.key], c.type)))
      .join(',')
  );
  const csv = [header, ...lines].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenameBase || 'assistant-export'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function formatCellValue(value: unknown, type?: string, formatCurrency?: (amount: number, code?: string) => string): string {
  if (value === null || value === undefined || value === '') return '—';
  if (type === 'currency') {
    const n = Number(value);
    if (Number.isFinite(n) && formatCurrency) {
      return formatCurrency(n);
    }
    if (Number.isFinite(n)) {
      return formatNumber(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
  if (type === 'date') {
    const formatted = formatDate(String(value));
    if (formatted) return formatted;
  }
  return String(value);
}

const MONEY_CHART_FIELDS = new Set([
  'amount',
  'revenue',
  'value',
  'stock_value',
  'total_outstanding',
  'total_overdue',
  'open_value',
  'grand_total',
  'balance_due',
  'gap',
  'invoiced',
  'collections',
]);

function isMoneyChartField(field: string | undefined): boolean {
  if (!field) return false;
  const key = field.toLowerCase();
  if (MONEY_CHART_FIELDS.has(key)) return true;
  return key.includes('amount') || key.includes('outstanding') || key.includes('overdue') || key.includes('value');
}

function chartAxisMoneyFormat(formatCurrency: (amount: number) => string) {
  return {
    y: {
      labelFormatter: (v: string | number) => {
        const n = Number(v);
        return Number.isFinite(n) ? formatCurrency(n) : String(v);
      },
    },
  };
}

export interface ToolResultCardProps {
  toolName: string;
  data: unknown;
  compact?: boolean;
  onActionPrompt?: (prompt: string) => void;
  onPin?: (input: {
    sourceType: 'kpi' | 'chart' | 'table' | 'report' | 'insight';
    title: string;
    payload: Record<string, unknown>;
  }) => void;
  onSaveUdq?: (input: {
    intent: string;
    filters?: Record<string, unknown>;
    query?: string;
    title?: string;
    displayColumns?: string[];
  }) => void;
}

function tryExtractSummaryFromToolData(data: unknown): DocumentSummary | null {
  if (isDocumentSummary(data)) {
    return data;
  }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const o = data as Record<string, unknown>;
    if (o.summary !== undefined && isDocumentSummary(o.summary)) {
      return o.summary;
    }
    if (o.found === true && o.summary !== undefined && isDocumentSummary(o.summary)) {
      return o.summary;
    }
  }
  return null;
}

/**
 * Renders structured tool output: 360° summaries use `SummaryCard`, otherwise a readable JSON/code block.
 */
export function ToolResultCard({ toolName, data, compact, onActionPrompt, onPin, onSaveUdq }: ToolResultCardProps) {
  const { token } = theme.useToken();
  const { formatCurrency } = useCurrency();
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const summary = tryExtractSummaryFromToolData(data);
  if (summary) {
    return <SummaryCard summary={summary} compact={compact} />;
  }

  if (isAssistantRichResponse(data) && isHowToGuidePayload(data.howTo)) {
    const chips = (Array.isArray(data.suggestedActions) ? data.suggestedActions : []).filter(
      (a): a is { label: string; prompt: string } =>
        typeof a === 'object' && !!a && typeof a.label === 'string' && typeof a.prompt === 'string' && a.label.startsWith('Did you mean')
    );
    return (
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        {chips.length && onActionPrompt ? (
          <Space wrap size={[6, 6]}>
            {chips.map((c) => (
              <Button key={c.label} size="small" onClick={() => onActionPrompt(c.prompt)}>
                {c.label}
              </Button>
            ))}
          </Space>
        ) : null}
        <HowToGuideCard
          payload={data.howTo}
          compact={compact}
          onRelated={onActionPrompt ? (_id, title) => onActionPrompt(`How do I ${title.toLowerCase()}?`) : undefined}
        />
      </Space>
    );
  }

  const nav = asNavigationResult(data);
  if (nav && !isAssistantRichResponse(data)) {
    return (
      <Card
        size="small"
        className="ai-agent-tool-result-card"
        styles={{ body: { padding: compact ? 8 : 12 } }}
        style={{ borderColor: token.colorBorderSecondary }}
      >
        <Link to={nav.path}>
          <Button type="link" style={{ padding: 0 }}>
            Open {nav.label}
          </Button>
        </Link>
        {nav.description ? (
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
            {nav.description}
          </Text>
        ) : null}
      </Card>
    );
  }

  if (isAssistantRichResponse(data)) {
    const kpis = Array.isArray(data.kpiCards) ? data.kpiCards : [];
    const table = data.dataTable;
    const chart = data.chartSpec;
    const warnings = Array.isArray(data.warnings) ? data.warnings : [];
    const links = Array.isArray(data.links) ? data.links : [];
    const suggestedActions = Array.isArray(data.suggestedActions) ? data.suggestedActions : [];
    const exportOptions = Array.isArray(data.exportOptions) ? data.exportOptions : [];
    const chartData = Array.isArray(chart?.data) ? chart!.data : [];
    const exportIntent = typeof data.intent === 'string' ? data.intent : undefined;
    const exportFilters =
      data.sourceContext?.filters && typeof data.sourceContext.filters === 'object'
        ? (data.sourceContext.filters as Record<string, unknown>)
        : undefined;
    const exportFilenameBase = (table?.title || exportIntent || 'erp-assistant-export')
      .replace(/\s+/g, '-')
      .toLowerCase();

    const handleDownloadXlsx = async () => {
      if (!table?.rows?.length) return;
      setXlsxLoading(true);
      try {
        await downloadAssistantTableXlsx({
          intent: exportIntent,
          filters: exportFilters,
          dataTable: exportIntent ? undefined : table,
          filenameBase: exportFilenameBase,
        });
      } catch (e) {
        message.error(e instanceof Error ? e.message : 'Excel export failed');
      } finally {
        setXlsxLoading(false);
      }
    };

    const handleDownloadPdf = async () => {
      if (!table?.rows?.length) return;
      setPdfLoading(true);
      try {
        await downloadAssistantTablePdf({
          intent: exportIntent,
          filters: exportFilters,
          dataTable: exportIntent ? undefined : table,
          filenameBase: exportFilenameBase,
        });
      } catch (e) {
        message.error(e instanceof Error ? e.message : 'PDF export failed');
      } finally {
        setPdfLoading(false);
      }
    };

    return (
      <Card
        size="small"
        className="ai-agent-tool-result-card"
        styles={{ body: { padding: compact ? 8 : 12 } }}
        style={{ borderColor: token.colorBorderSecondary }}
        extra={
          onPin || onSaveUdq ? (
            <Space size={4}>
              {exportIntent && onSaveUdq && (table || kpis.length) ? (
                <Button
                  type="text"
                  size="small"
                  icon={<SaveOutlined />}
                  title="Save as UDQ"
                  onClick={() =>
                    onSaveUdq({
                      intent: exportIntent,
                      filters: exportFilters,
                      title: table?.title || exportIntent,
                      displayColumns: table?.columns?.map((c) => c.key),
                    })
                  }
                />
              ) : null}
              {onPin && kpis.length === 1 ? (
                <Button
                  type="text"
                  size="small"
                  icon={<PushpinOutlined />}
                  onClick={() =>
                    onPin({
                      sourceType: 'kpi',
                      title: kpis[0].label,
                      payload: { kpi: kpis[0], intent: exportIntent },
                    })
                  }
                />
              ) : null}
              {onPin && chart ? (
                <Button
                  type="text"
                  size="small"
                  icon={<PushpinOutlined />}
                  onClick={() =>
                    onPin({
                      sourceType: 'chart',
                      title: chart.title || exportIntent || 'Chart',
                      payload: { chartSpec: chart, intent: exportIntent },
                    })
                  }
                />
              ) : null}
              {onPin && table ? (
                <Button
                  type="text"
                  size="small"
                  icon={<PushpinOutlined />}
                  onClick={() =>
                    onPin({
                      sourceType: 'table',
                      title: table.title || exportIntent || 'Table',
                      payload: { dataTable: table, intent: exportIntent, filters: exportFilters },
                    })
                  }
                />
              ) : null}
            </Space>
          ) : null
        }
      >
        {data.answerText ? (
          <Paragraph style={{ marginTop: 0, whiteSpace: 'pre-wrap' }}>{data.answerText}</Paragraph>
        ) : null}

        {kpis.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 8,
              marginBottom: table || chart ? 12 : 0,
            }}
          >
            {kpis.map((kpi, idx) => (
              <Card key={`${kpi.label}-${idx}`} size="small">
                {kpi.format === 'currency' ? (
                  <Statistic
                    title={kpi.label}
                    value={formatCurrency(Number(kpi.value))}
                    valueStyle={{ fontSize: 20 }}
                  />
                ) : (
                  <Statistic title={kpi.label} value={kpi.value as any} />
                )}
              </Card>
            ))}
          </div>
        ) : null}

        {table ? (
          <div style={{ marginBottom: chart ? 12 : 0 }}>
            {table.title ? <Text strong style={{ display: 'block', marginBottom: 8 }}>{table.title}</Text> : null}
            <Table
              size="small"
              rowKey={(_, i) => String(i)}
              pagination={{ pageSize: compact ? 5 : 8 }}
              dataSource={Array.isArray(table.rows) ? table.rows : []}
              columns={(Array.isArray(table.columns) ? table.columns : []).map((col) => ({
                title: col.label,
                dataIndex: col.key,
                key: col.key,
                render: (value: unknown) => formatCellValue(value, col.type, formatCurrency),
              }))}
              scroll={{ x: true }}
            />
          </div>
        ) : null}

        {chart && chartData.length > 0 ? (
          <div>
            {chart.title ? <Text strong style={{ display: 'block', marginBottom: 8 }}>{chart.title}</Text> : null}
            {chart.type === 'line' ? (
              <Line
                data={chartData}
                xField={chart.xField || 'x'}
                yField={chart.yField || 'y'}
                {...(isMoneyChartField(chart.yField) ? { axis: chartAxisMoneyFormat(formatCurrency) } : {})}
              />
            ) : chart.type === 'area' ? (
              <Area
                data={chartData}
                xField={chart.xField || 'x'}
                yField={chart.yField || 'y'}
                {...(isMoneyChartField(chart.yField) ? { axis: chartAxisMoneyFormat(formatCurrency) } : {})}
              />
            ) : chart.type === 'pie' || chart.type === 'donut' ? (
              <Pie
                data={chartData}
                angleField={chart.angleField || chart.yField || 'value'}
                colorField={chart.colorField || chart.xField || 'category'}
                innerRadius={chart.type === 'donut' ? 0.6 : 0}
              />
            ) : (
              <Column
                data={chartData}
                xField={chart.xField || 'x'}
                yField={chart.yField || 'y'}
                {...(chart.type === 'stacked' ? { stack: true } : {})}
                {...(isMoneyChartField(chart.yField) ? { axis: chartAxisMoneyFormat(formatCurrency) } : {})}
              />
            )}
          </div>
        ) : null}

        {links.length > 0 ? (
          <div style={{ marginTop: 10 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Related Links</Text>
            <Space wrap>
              {links.map((link, idx) => {
              const path = String(link?.path || '').trim();
              const label = String(link?.label || path || 'Open').trim();
              if (!path) return null;
              const isExternal = /^https?:\/\//i.test(path);
              const isSafeInternal = path.startsWith('/');
              if (!isExternal && !isSafeInternal) return null;
              return (
                isExternal ? (
                  <Button key={`${path}-${idx}`} type="link" href={path} target="_blank" rel="noreferrer">
                    {label}
                  </Button>
                ) : (
                  <Link key={`${path}-${idx}`} to={path}>
                    <Button type="link">{label}</Button>
                  </Link>
                )
              );
              })}
            </Space>
          </div>
        ) : null}

        {suggestedActions.length > 0 ? (
          <div style={{ marginTop: 10 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Suggested Next Actions</Text>
            <Space wrap>
              {suggestedActions.map((action, idx) => {
                const isText = typeof action === 'string';
                const label = isText ? action : String(action?.label || '').trim();
                const path = isText ? '' : String(action?.path || '').trim();
                const actionPrompt = isText ? '' : String(action?.prompt || '').trim();
                if (!label) return null;
                if (actionPrompt && onActionPrompt) {
                  return (
                    <Button
                      key={`${label}-${idx}`}
                      size="small"
                      onClick={() => onActionPrompt(actionPrompt)}
                    >
                      {label}
                    </Button>
                  );
                }
                if (!path) {
                  return (
                    <Text key={`${label}-${idx}`} style={{ display: 'block' }}>
                      - {label}
                    </Text>
                  );
                }
                const isExternal = /^https?:\/\//i.test(path);
                const isSafeInternal = path.startsWith('/');
                if (!isExternal && !isSafeInternal) return null;
                return isExternal ? (
                  <Button key={`${path}-${idx}`} type="link" href={path} target="_blank" rel="noreferrer">
                    {label}
                  </Button>
                ) : (
                  <Link key={`${path}-${idx}`} to={path}>
                    <Button type="link">{label}</Button>
                  </Link>
                );
              })}
            </Space>
          </div>
        ) : null}

        {exportOptions.includes('csv') && table && table.rows.length > 0 ? (
          <div style={{ marginTop: 10 }}>
            <Space wrap>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() =>
                  downloadAssistantTableCsv(
                    table,
                    exportFilenameBase
                  )
                }
              >
                Download CSV
              </Button>
              {exportOptions.includes('xlsx') ? (
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  loading={xlsxLoading}
                  onClick={() => void handleDownloadXlsx()}
                >
                  Download Excel
                </Button>
              ) : null}
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={pdfLoading}
                onClick={() => void handleDownloadPdf()}
              >
                Download PDF
              </Button>
            </Space>
          </div>
        ) : null}

        {warnings.length > 0 ? (
          <div style={{ marginTop: 8 }}>
            {warnings.map((w, idx) => (
              <Text key={`${w}-${idx}`} type="warning" style={{ display: 'block' }}>
                {w}
              </Text>
            ))}
          </div>
        ) : null}
      </Card>
    );
  }

  if (toolName === 'queryERPData' && isQueryErpDataRow(data)) {
    const title = humanizeQueryIntent(data.intent);
    const hasCount = typeof data.count === 'number';
    const isOk = data.ok === true;
    return (
      <Card
        size="small"
        className="ai-agent-tool-result-card"
        styles={{ body: { padding: compact ? 8 : 12 } }}
        style={{ borderColor: token.colorBorderSecondary }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: hasCount || isOk ? 8 : 0 }}>
          <DatabaseOutlined style={{ color: token.colorPrimary }} />
          <Text strong style={{ flex: 1 }}>
            {title}
          </Text>
        </div>
        {hasCount && (
          <Statistic value={data.count} valueStyle={{ fontSize: 22, color: token.colorTextHeading }} />
        )}
        {isOk && !hasCount && (
          <Text type="success">Tenant / company context OK</Text>
        )}
        {!hasCount && !isOk && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {JSON.stringify(data)}
          </Text>
        )}
      </Card>
    );
  }

  let body: string;
  try {
    body = JSON.stringify(data, null, 2) ?? String(data);
  } catch {
    body = String(data);
  }

  return (
    <Card
      size="small"
      title={toolName}
      className="ai-agent-tool-result-card"
      styles={{ body: { padding: compact ? 8 : 12 } }}
    >
      <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
        {body}
      </Paragraph>
    </Card>
  );
}
